import { useTranslation } from 'next-i18next';
import { FC, useCallback, useEffect, useRef } from 'react';
import {
  IWedding_Website_Section,
  IWedding_Website_Section_VenueField,
} from '@bridebook/models/source/models/Weddings.types';
import { mapSlugToFriendlyUrlSlug } from '@bridebook/toolbox';
import { Box } from '@bridebook/ui';
import { IconVenue } from '@bridebook/ui/src/icons/dynamic';
import { Link } from '@bridebook/web/app-shared/components/link/link';
import {
  WebsiteTileVariant,
  WeddingWebsiteTileComponent,
} from '@bridebook/web/components/wedding-website/view-mode/partials/tile-component/wedding-website-tile-component';
import {
  useViewModeAnalytics,
  useViewModeSuppliers,
} from '@bridebook/web/components/wedding-website/view-mode/view-mode.context';
import { SupplierMap } from '@bridebook/web/components/wedding-website/view-mode/website-view/tiles/supplier-map';
import { isCustomSupplier } from '@bridebook/web/lib/supplier/hooks/query/use-supplier';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { useDispatch } from '@bridebook/web/lib/utils';
import { interactedWithWeddingWebsiteAnalyticsButtonClick } from '@bridebook/web/lib/wedding-website/analytics';
import { Palettes } from '@bridebook/web/lib/wedding-website/types';
import { FloatingEditButton } from '@bridebook/web/components/wedding-website/view-mode/partials/floating-edit-button/floating-edit-button';
import { shouldShowWebsiteSection } from '@bridebook/web/lib/wedding-website/utils/should-show-website-section';
import { useEditorSyncContext } from '@bridebook/web/components/wedding-website/main/desktop/editor-sync-context';
import { getSupplierAddressText } from '@bridebook/web/lib/wedding-website/utils/get-supplier-address-text';

interface IProps {
  section: IWedding_Website_Section_VenueField;
  variant: WebsiteTileVariant;
  palette: Palettes;
}

export const canShowWeddingWebsiteVenueSection = (
  isPreviewMode: boolean,
  section: IWedding_Website_Section,
) => {
  if (section.type !== 'VenueField') throw new Error('Incorrect section');

  return isPreviewMode || !!section.data.supplier;
};

export const WeddingWebsiteVenue: FC<IProps> = ({ variant, section, palette }) => {
  const { t } = useTranslation('weddingWebsite');
  const dispatch = useDispatch();
  const { registerPreviewRef, triggerDetailsSync } = useEditorSyncContext();
  const tileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    registerPreviewRef('venue', tileRef.current);
  }, [registerPreviewRef]);

  const handleEditVenue = useCallback(() => {
    triggerDetailsSync('venue');
    dispatch(
      interactedWithWeddingWebsiteAnalyticsButtonClick({
        section: 'weddingLocation',
        element: 'editButton',
      }),
    );
  }, [dispatch, triggerDetailsSync]);
  const { byId } = useViewModeSuppliers();
  const sendAnalytics = useViewModeAnalytics();
  const supplier = byId[section.data.supplier];

  if (!shouldShowWebsiteSection(section)) {
    return null;
  }
  const defaultName = t('previewMode.tile.ourVenue.title');

  const hasBBSupplier = !!supplier && !isCustomSupplier(supplier);

  const supplierUrl = !hasBBSupplier ? undefined : `${supplier.seo?.urlSlug}-${supplier.publicId}`;
  const supplierType = !hasBBSupplier
    ? undefined
    : mapSlugToFriendlyUrlSlug(supplier.type[0]).newSlug;
  const title = supplier?.name ?? defaultName;

  return (
    <div ref={tileRef} className="group w-full">
      <WeddingWebsiteTileComponent title={title} variant={variant} palette={palette}>
        <WeddingWebsiteTileComponent.Container>
          <WeddingWebsiteTileComponent.ItemGroup>
            <WeddingWebsiteTileComponent.Text>
              {getSupplierAddressText(supplier)}
              {!supplier && (
                <WeddingWebsiteTileComponent.TextPlaceholder>
                  {t('previewMode.tile.venue.toBeConfirmed')}
                </WeddingWebsiteTileComponent.TextPlaceholder>
              )}
            </WeddingWebsiteTileComponent.Text>
          </WeddingWebsiteTileComponent.ItemGroup>

          {hasBBSupplier && <SupplierMap supplier={supplier} />}

          {supplierUrl && supplierType && (
            <Box style={{ alignSelf: 'center' }} onClick={sendAnalytics}>
              <Link
                href={`${UrlHelper.suppliers.supplier(
                  supplierType,
                  supplierUrl,
                )}?viewedLocation=weddingWebsites`}>
                <WeddingWebsiteTileComponent.Button
                  variant={palette === Palettes.Bridebook ? 'secondary' : 'primary'}
                  startIcon={<IconVenue width={16} />}>
                  {t('previewMode.tile.venue.viewOnBridebook')}
                </WeddingWebsiteTileComponent.Button>
              </Link>
            </Box>
          )}
        </WeddingWebsiteTileComponent.Container>
        <FloatingEditButton
          href={UrlHelper.website.weddingDetails.sectionAnchor('venue')}
          onClick={handleEditVenue}
        />
      </WeddingWebsiteTileComponent>
    </div>
  );
};
